/**
 * PrivacyPage — full-page privacy policy.
 * Accessible at /privacy route (SHA-12).
 *
 * Mirrors TermsPage structure. Reachable both logged-in (via the
 * dashboard nav / activePage === 'privacy') and logged-out (SHA-24 —
 * see App.tsx), so `onBack`/`backLabel` can be overridden by the caller
 * when there's no dashboard to return to.
 */

import { BrandLogo } from '../BrandLogo';
import { usePaperTradingStore } from '../../stores/paperTradingStore';

// ---------------------------------------------------------------------------
// Section heading
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="space-y-3">
      <h2 className="font-display font-bold text-lg text-white">{title}</h2>
      <div className="space-y-2 text-sm text-gray-300 leading-relaxed">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface PrivacyPageProps {
  /**
   * Optional override for the back navigation target — used when the page
   * is reachable while logged out (no dashboard to return to). Defaults to
   * navigating the paper trading store back to the backtesting page.
   */
  onBack?: () => void;
  /** Label for the back button. Defaults to "Back to dashboard". */
  backLabel?: string;
}

export function PrivacyPage({ onBack, backLabel }: PrivacyPageProps = {}) {
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);
  const handleBack = onBack ?? (() => setActivePage('backtesting'));
  const label = backLabel ?? 'Back to dashboard';

  return (
    <div className="min-h-screen app-bg py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-10">
        {/* Header */}
        <div className="space-y-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {label}
          </button>

          <BrandLogo markClassName="w-8 h-8" wordClassName="text-lg" />

          <div>
            <h1 className="font-display font-extrabold text-3xl text-white">Privacy Policy</h1>
            <p className="text-sm text-gray-500 mt-1">Last updated: 2026-07-05</p>
          </div>
        </div>

        {/* Content */}
        <div className="glass-panel rounded-2xl p-8 sm:p-10 space-y-8">
          <Section title="Overview">
            <p>
              sharpelab is an educational crypto-strategy backtesting and paper-trading tool.
              This notice explains what we store and why. We keep data collection minimal.
            </p>
          </Section>

          <Section title="What we collect">
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                <span className="font-semibold text-white">Account:</span> a username and a
                securely hashed password. We do not collect your email address or real name.
              </li>
              <li>
                <span className="font-semibold text-white">API / MCP tokens:</span> a hashed
                version of the access tokens you generate to connect AI clients (e.g. Claude
                Desktop). We cannot recover the raw token — only verify it.
              </li>
              <li>
                <span className="font-semibold text-white">Your content:</span> strategies you
                create, backtest and grid-search results, paper-trading sessions, and their
                configurations.
              </li>
              <li>
                <span className="font-semibold text-white">Optional Telegram link:</span> if you
                connect Telegram for notifications, we store your Telegram chat ID. You can
                disconnect at any time.
              </li>
              <li>
                <span className="font-semibold text-white">Operational logs:</span> basic request
                and error logs for reliability and abuse prevention. We avoid logging secrets and
                tokens.
              </li>
            </ul>
          </Section>

          <Section title="What we do NOT do">
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>We do not sell or rent your data.</li>
              <li>We do not use your data for advertising.</li>
              <li>We do not require or store payment information (currently free).</li>
              <li>
                We do not connect to real exchange accounts or move real funds (paper trading
                only at this stage).
              </li>
            </ul>
          </Section>

          <Section title="Where data lives">
            <p>
              PostgreSQL on our own server infrastructure. Market data is fetched from public
              exchange APIs (e.g. Bybit).
            </p>
          </Section>

          <Section title="Retention & deletion">
            <p>
              We keep your data while your account is active. You can delete your strategies,
              backtests, and sessions from the dashboard. To delete your account and all
              associated data, contact us via the project&apos;s GitHub issues.
            </p>
          </Section>

          <Section title="Security">
            <p>
              Passwords and tokens are stored hashed. Reasonable measures, no absolute guarantee.
              Do not store sensitive personal information in strategy names/descriptions.
            </p>
          </Section>

          {/* SHA-12 user-requested extension: GDPR alignment */}
          <Section title="GDPR — lawful basis and your rights">
            <p>
              For users in the European Economic Area (EEA) and United Kingdom, we process
              personal data on the following lawful bases: your <span className="font-semibold text-white">consent</span> (e.g.
              creating an account, connecting Telegram) and our <span className="font-semibold text-white">legitimate interest</span>{' '}
              in operating, securing, and improving the Service. Because we already collect the
              minimum data needed to run the Service (no email address, no real name, no payment
              details), data minimization is built into the design rather than an afterthought.
            </p>
            <p>Subject to applicable law, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li><span className="font-semibold text-white">Access</span> the personal data we hold about you.</li>
              <li><span className="font-semibold text-white">Rectification</span> of inaccurate or incomplete data.</li>
              <li><span className="font-semibold text-white">Erasure</span> of your data ("right to be forgotten").</li>
              <li><span className="font-semibold text-white">Portability</span> — receive your data in a portable format.</li>
              <li><span className="font-semibold text-white">Object</span> to processing based on legitimate interest.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us via GitHub issues on the project
              repository, referencing your account username. We will respond within a reasonable
              timeframe consistent with applicable law.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              We may update this policy; material changes reflected in the &quot;Last updated&quot;
              date and re-prompted on next login where appropriate.
            </p>
          </Section>

          <Section title="Contact">
            <p>Questions? Reach us via GitHub issues on the project repository.</p>
          </Section>
        </div>
      </div>
    </div>
  );
}
